import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { Loan, LoanType } from "@/types";
import NumberInput from "@/components/base/NumberInput";
import { formatCOP, formatDate, uid } from "@/utils/format";

interface LoansPanelProps {
  loans: Loan[];
  onAdd: (l: Loan) => void;
  onDelete: (id: string) => void;
}

const LoansPanel = ({ loans, onAdd, onDelete }: LoansPanelProps) => {
  const [open, setOpen] = useState(false);
  const [concept, setConcept] = useState("");
  const [value, setValue] = useState(0);
  const [type, setType] = useState<LoanType>("Préstamo");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setConcept("");
    setValue(0);
    setType("Préstamo");
    setDate(new Date().toISOString().slice(0, 10));
    setError("");
  }, [open]);

  const lent = loans
    .filter((l) => l.type === "Préstamo")
    .reduce((acc, l) => acc + l.value, 0);
  const returned = loans
    .filter((l) => l.type === "Devolución")
    .reduce((acc, l) => acc + l.value, 0);
  const balance = lent - returned;

  const submit = () => {
    if (!concept.trim()) {
      setError("Escribe el concepto del movimiento.");
      return;
    }
    if (Number(value) <= 0) {
      setError("El valor debe ser mayor a cero.");
      return;
    }
    onAdd({
      id: uid("loan"),
      date: new Date(date).toISOString(),
      concept: concept.trim(),
      value: Number(value) || 0,
      type,
    });
    setOpen(false);
  };

  return (
    <div className="glass-panel rounded-lg">
      <div className="px-5 py-4 border-b border-accent-500/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-md bg-accent-500/20 text-accent-200 flex items-center justify-center">
            <i className="ri-exchange-dollar-line" />
          </span>
          <div>
            <h3 className="editable-title text-base font-bold">Préstamos Juank.unlimited</h3>
            <p className="text-[11px] text-foreground-500">Cuenta inter-empresa de préstamos y devoluciones</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
        >
          <i className="ri-add-line" /> Nuevo movimiento
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 px-5 py-4 border-b border-accent-500/10">
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Prestado</p>
          <p className="text-sm font-bold text-foreground-950">{formatCOP(lent)}</p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Devuelto</p>
          <p className="text-sm font-bold text-accent-200">{formatCOP(returned)}</p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Saldo en la calle</p>
          <p className="text-sm font-bold text-foreground-950">{formatCOP(balance)}</p>
        </div>
      </div>

      {loans.length === 0 ? (
        <p className="px-5 py-8 text-center text-xs text-foreground-500">
          Aún no hay movimientos de préstamo registrados.
        </p>
      ) : (
        <>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-accent-500/15">
                <th className="text-left font-semibold px-5 py-3">Fecha</th>
                <th className="text-left font-semibold px-5 py-3">Concepto</th>
                <th className="text-center font-semibold px-5 py-3">Tipo</th>
                <th className="text-right font-semibold px-5 py-3">Valor</th>
                <th className="text-center font-semibold px-5 py-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => (
                <tr key={l.id} className="border-b border-background-300/20 hover:bg-background-200/30 transition-colors">
                  <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">{formatDate(l.date)}</td>
                  <td className="px-5 py-3 text-foreground-950">{l.concept}</td>
                  <td className="px-5 py-3 text-center">
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        l.type === "Préstamo"
                          ? "bg-secondary-500/20 text-secondary-100"
                          : "bg-accent-500/20 text-accent-200"
                      }`}
                    >
                      {l.type}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-foreground-950">{formatCOP(l.value)}</td>
                  <td className="px-5 py-3">
                    {confirmId === l.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            onDelete(l.id);
                            setConfirmId(null);
                          }}
                          className="px-2.5 py-1 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                        >
                          Sí
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="px-2 py-1 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmId(l.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer mx-auto"
                        aria-label="Eliminar movimiento"
                        title="Eliminar movimiento"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="md:hidden divide-y divide-background-300/25">
          {loans.map((l) => (
            <li key={l.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 break-words">{l.concept}</p>
                  <p className="text-[10px] text-foreground-500 mt-0.5">{formatDate(l.date)}</p>
                </div>
                <span
                  className={`shrink-0 inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold ${
                    l.type === "Préstamo"
                      ? "bg-secondary-500/20 text-secondary-100"
                      : "bg-accent-500/20 text-accent-200"
                  }`}
                >
                  {l.type}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm font-bold text-foreground-950">{formatCOP(l.value)}</span>
                {confirmId === l.id ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        onDelete(l.id);
                        setConfirmId(null);
                      }}
                      className="px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Sí
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-3 py-1.5 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(l.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                    aria-label="Eliminar movimiento"
                    title="Eliminar movimiento"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
        </>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nuevo movimiento de préstamo"
        icon="ri-exchange-dollar-line"
        footer={
          <>
            <button onClick={() => setOpen(false)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button onClick={submit} className={primaryButtonClass}>
              <i className="ri-save-3-line" /> Guardar movimiento
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Concepto</label>
            <input
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              placeholder="Ej: Préstamo a Juanka para compra de inventario"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Tipo</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as LoanType)}
                className={inputClass}
              >
                <option value="Préstamo" className="bg-background-100">
                  Préstamo
                </option>
                <option value="Devolución" className="bg-background-100">
                  Devolución
                </option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Valor (COP)</label>
              <NumberInput min={0} value={value} onChange={setValue} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Fecha</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          {error && (
            <p className="text-[11px] text-primary-200 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {error}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LoansPanel;